/**
 * Guest Guard Component
 *
 * Protects routes that should only be accessible to unauthenticated users.
 * Redirects to dashboard if user is already authenticated.
 *
 * Typically used for login/register pages.
 */

import { Navigate, useLocation } from 'react-router-dom';

import { useAuthStore } from '@/store';
import { getStoredToken } from '@/utils/storage.utils';

interface GuestGuardProps {
  children: React.ReactNode;
}

interface LocationState {
  from?: {
    pathname: string;
  };
}

/**
 * GuestGuard - Protects guest-only routes (login, register)
 *
 * @example
 * <Route
 *   path="/login"
 *   element={
 *     <GuestGuard>
 *       <LoginPage />
 *     </GuestGuard>
 *   }
 * />
 */
export function GuestGuard({ children }: GuestGuardProps) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Check both store state and token existence
  const hasToken = !!getStoredToken();
  const isLoggedIn = isAuthenticated && hasToken;

  if (isLoggedIn) {
    // Redirect to the page they tried to visit, or dashboard
    const state = location.state as LocationState | null;
    const from = state?.from?.pathname || '/dashboard';

    return <Navigate to={from} replace />;
  }

  // Not authenticated - render children (login page)
  return <>{children}</>;
}
